/**
 * useBillingSession — Day Open / Day Close lifecycle for desktop billing.
 *
 * Desktop subset of BigxBookApp/src/billing/useBillingSession.ts:
 *   ✓ Reactive query for today's OPEN session (per store + device)
 *   ✓ openSession(openingCash) — creates row with isSynced=false
 *   ✓ closeSession(actualCash) — computes expected vs variance, marks CLOSED
 *   ✓ getSessionSummary() — opening + cash sales + cash expenses → expected
 *   ✓ checkBillingGate(storeId, role) non-hook utility for useCreateInvoice
 *
 * Differences vs mobile:
 *   ✗ No `useAppStore.currentBillingSessionId` — desktop reads live from DB
 *     (the WatermelonDB observable already keeps everything in sync, and
 *     keeping it out of Zustand means there's only one source of truth).
 *   ✓ cashExpenses — queries `expenses` table for today's CASH expenses.
 */

import { useCallback, useEffect, useState } from 'react';
import { Q } from '@nozbe/watermelondb';
import { summariseSessionCash } from './billingEngine';
import { database, getActiveDatabase, txn } from '@/db';
import { BillingSession, Invoice, Payment } from '@/db/models/Billing';
import { Expense } from '@/db/models/System';
import Store from '@/db/models/Store';
import { useAppStore } from '@/store/useAppStore';
import { logActivity } from '@/utils/auditLog';

export interface SessionSummary {
    openingCash: number;
    cashSales: number;
    /** Cash handed back across the counter for returns during this session. */
    cashRefunds: number;
    cashExpenses: number;
    expectedCash: number;
}

export interface BillingSessionState {
    activeSession: BillingSession | null;
    isLoading: boolean;
    isSessionOpen: boolean;
    openSession: (openingCash: number) => Promise<BillingSession>;
    closeSession: (actualCash: number) => Promise<SessionSummary>;
    getSessionSummary: () => Promise<SessionSummary | null>;
    refresh: () => void;
}

function startOfToday(): number {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.getTime();
}

export function useBillingSession(): BillingSessionState {
    const activeStoreId = useAppStore((s) => s.activeStoreId);
    const currentUser = useAppStore((s) => s.currentUser);
    const deviceId = useAppStore((s) => s.deviceId);

    const [activeSession, setActiveSession] = useState<BillingSession | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [refreshTrigger, setRefreshTrigger] = useState(0);

    // Reactive subscription to today's OPEN session for this store.
    useEffect(() => {
        if (!activeStoreId || !deviceId) {
            setActiveSession(null);
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        const sub = database
            .get<BillingSession>('billing_sessions')
            .query(
                Q.where('store_id', activeStoreId),
                // #7 fix: sessions are per-device (one cash drawer per counter). Without
                // this filter, after sync a counter attaches its sales to another
                // counter's open session.
                Q.where('device_id', deviceId),
                Q.where('status', 'OPEN'),
                Q.where('is_deleted', false),
                Q.where('session_date', Q.gte(startOfToday())),
            )
            .observe()
            .subscribe({
                next: (sessions) => {
                    setActiveSession(sessions.length > 0 ? sessions[0] : null);
                    setIsLoading(false);
                },
                error: (err) => {
                    console.warn('[useBillingSession] Query failed:', err?.message);
                    setActiveSession(null);
                    setIsLoading(false);
                },
            });
        return () => sub.unsubscribe();
    }, [activeStoreId, deviceId, refreshTrigger]);

    const openSession = useCallback(async (openingCash: number): Promise<BillingSession> => {
        if (!activeStoreId || !currentUser || !deviceId) {
            throw new Error('Cannot open session: missing store, user, or device id');
        }
        // Re-check immediately before writing to defend against double-click /
        // dual-window races on the same desktop machine.
        const existing = await database
            .get<BillingSession>('billing_sessions')
            .query(
                Q.where('store_id', activeStoreId),
                Q.where('device_id', deviceId), // #7: per-device — other counters may open their own
                Q.where('status', 'OPEN'),
                Q.where('is_deleted', false),
                Q.where('session_date', Q.gte(startOfToday())),
            )
            .fetch();
        if (existing.length > 0) {
            throw new Error('A session is already open for this counter today');
        }

        const now = new Date();
        const session = await txn(async (db) =>
            db.get<BillingSession>('billing_sessions').create((record) => {
                record.storeId = activeStoreId;
                record.deviceId = deviceId;
                record.userId = currentUser.id;
                record.sessionDate = now;
                record.openedAt = now;
                record.closedAt = null;
                record.closedById = null;
                record.openingCash = Math.max(0, openingCash);
                record.closingCash = null;
                record.expectedCash = null;
                record.variance = null;
                record.status = 'OPEN';
                record.version = 1;
                record.isSynced = false;
                record.isDeleted = false;
            }),
        );
        void logActivity({
            action: 'day_opened',
            entityType: 'billing_session',
            entityId: session.id,
            storeId: activeStoreId,
            userId: currentUser.id,
            changes: {
                summary: `Opening cash ${Math.max(0, openingCash)}`,
                amount: Math.max(0, openingCash),
            },
        });
        return session;
    }, [activeStoreId, currentUser, deviceId]);

    const getSessionSummary = useCallback(async (): Promise<SessionSummary | null> => {
        if (!activeSession || !activeStoreId) return null;

        // ── What the drawer should hold ──
        //
        // Everything is scoped to the SESSION now, not to the session's invoices
        // and not to the calendar day. The old basis could not express three
        // things a shop does daily:
        //
        //   1. A khata payment collected today against last week's invoice. The
        //      money is in the drawer; the invoice belonged to a closed session,
        //      so the count never saw it.
        //   2. A refund paid out of the drawer. Credit notes were excluded
        //      outright (the query filtered type = 'SALE'), so cash leaving the
        //      till was invisible.
        //   3. Two counters open at once. Expenses were store-wide for the day,
        //      so the same expense was charged against every drawer.
        //
        // Rows written before the billing_session_id columns existed carry null
        // and are simply not this session's — correct for a closed session, and
        // for one open across the upgrade it means the count starts from the
        // upgrade rather than inventing history.
        const payments = await database
            .get<Payment>('payments')
            .query(
                Q.where('billing_session_id', activeSession.id),
                Q.where('method', 'CASH'),
                Q.where('is_deleted', false),
            )
            .fetch();

        // A payment on a CREDIT_NOTE is money leaving the till, not entering it.
        const invoiceIds = Array.from(new Set(payments.map((p) => p.invoiceId)));
        const invoiceTypeById = new Map<string, string>();
        for (let i = 0; i < invoiceIds.length; i += 500) {
            const rows = await database
                .get<Invoice>('invoices')
                .query(Q.where('id', Q.oneOf(invoiceIds.slice(i, i + 500))))
                .fetch();
            for (const inv of rows) invoiceTypeById.set(inv.id, inv.type);
        }

        // Cash expenses booked against THIS drawer.
        const expenses = await database
            .get<Expense>('expenses')
            .query(
                Q.where('billing_session_id', activeSession.id),
                Q.where('is_deleted', false),
            )
            .fetch();

        return summariseSessionCash({
            openingCash: activeSession.openingCash,
            payments: payments.map((p) => ({
                amount: p.amount,
                invoiceType: invoiceTypeById.get(p.invoiceId) ?? 'SALE',
            })),
            expenses: expenses.map((e) => ({ amount: e.amount, paymentMethod: e.paymentMethod })),
        });
    }, [activeSession, activeStoreId]);

    const closeSession = useCallback(async (actualCash: number): Promise<SessionSummary> => {
        if (!activeSession || !currentUser) {
            throw new Error('No active session to close');
        }
        const summary = await getSessionSummary();
        if (!summary) throw new Error('Could not compute session summary');

        const variance = actualCash - summary.expectedCash;
        await txn(async (db) => {
            await activeSession.update((record) => {
                record.closedAt = new Date();
                record.closedById = currentUser.id;
                record.closingCash = actualCash;
                record.expectedCash = summary.expectedCash;
                record.variance = variance;
                record.status = 'CLOSED';
                record.isSynced = false;
            });
        });
        void logActivity({
            action: 'day_closed',
            entityType: 'billing_session',
            entityId: activeSession.id,
            storeId: activeSession.storeId,
            userId: currentUser.id,
            changes: {
                summary: `Closed · counted ${actualCash} · expected ${summary.expectedCash} · variance ${variance >= 0 ? '+' : ''}${variance}`,
                amount: actualCash,
                after: {
                    openingCash: summary.openingCash,
                    cashSales: summary.cashSales,
                    cashExpenses: summary.cashExpenses,
                    expectedCash: summary.expectedCash,
                    closingCash: actualCash,
                    variance,
                },
            },
        });
        return summary;
    }, [activeSession, currentUser, getSessionSummary]);

    const refresh = useCallback(() => setRefreshTrigger((n) => n + 1), []);

    return {
        activeSession,
        isLoading,
        isSessionOpen: !!activeSession,
        openSession,
        closeSession,
        getSessionSummary,
        refresh,
    };
}

// ═════════════════════════════════════════════════════════════════════
//  Non-hook utility for useCreateInvoice
// ═════════════════════════════════════════════════════════════════════

export type BillingGate =
    | { allowed: true; sessionId: string | null; isEmergency: boolean }
    | { allowed: false; reason: string };

/**
 * Check if billing is allowed for the given store, returning either the
 * session id to attach to the new Invoice or a user-facing reason to refuse.
 *
 * Mirrors the mobile implementation 1:1 so behaviour stays identical
 * across surfaces; if mobile relaxes/tightens the rule, copy the diff here.
 */
/**
 * The open session for this store+device, or null.
 *
 * Exported so anything that moves cash — a refund, an expense — can stamp the
 * drawer it crossed. Day close used to infer the session from the payment's
 * INVOICE, which cannot see a khata payment collected today against last week's
 * invoice, and treated expenses as store-wide for the calendar day, so two
 * counters each absorbed the same one.
 *
 * Per device, matching the billing gate: two counters in one shop are two
 * drawers, and a session belongs to the device that opened it.
 */
export async function findOpenSession(
    storeId: string,
    deviceId: string,
): Promise<BillingSession | null> {
    const sessions = await getActiveDatabase()
        .get<BillingSession>('billing_sessions')
        .query(
            Q.where('store_id', storeId),
            Q.where('device_id', deviceId),
            Q.where('status', 'OPEN'),
            Q.where('is_deleted', false),
            Q.where('session_date', Q.gte(startOfToday())),
        )
        .fetch();
    return sessions.length > 0 ? sessions[0] : null;
}

/** Convenience for callers that only need the id to stamp onto a row. */
export async function findOpenSessionId(
    storeId: string,
    deviceId: string,
): Promise<string | null> {
    return (await findOpenSession(storeId, deviceId))?.id ?? null;
}

export async function checkBillingGate(
    storeId: string,
    userRole: string,
    deviceId: string,
): Promise<BillingGate> {
    const store = await database.get<Store>('stores').find(storeId);


    if (!store.requireDayOpen) {
        const open = await findOpenSession(storeId, deviceId);
        return { allowed: true, sessionId: open?.id ?? null, isEmergency: false };
    }

    const open = await findOpenSession(storeId, deviceId);
    if (open) return { allowed: true, sessionId: open.id, isEmergency: false };

    // No open session — check emergency-bill role allow-list.
    const emergencyRolesStr = store.emergencyBillRoles;
    if (emergencyRolesStr) {
        try {
            const emergencyRoles: string[] = JSON.parse(emergencyRolesStr);
            if (emergencyRoles.includes(userRole)) {
                return { allowed: true, sessionId: null, isEmergency: true };
            }
        } catch {
            // Malformed JSON in settings — treat as no emergency roles.
        }
    }

    return {
        allowed: false,
        reason: 'Open the day before billing. Ask your manager or owner to start the day.',
    };
}
